import clsx from 'clsx';
import CircleTickIcon from 'images/icons/CircleTick.svg';
import LocationIcon from 'images/icons/Location.svg';
import { ReviewSource } from 'types/review';
import {
  ExtendedCarouselWidgetSettings,
  ExtendedFullPageWidgetSettings,
  ExtendedReviewsTabWidgetSettings,
  ExtendedReviewsWidgetSettings,
  ExtendedTopRatedProductsWidgetSettings,
} from 'types/settings';

import { Icon } from 'components/Icon';
import { useSettings } from 'components/SettingsProvider';
import { Stack } from 'components/Stack';
import { Stars } from 'components/Stars';
import { Text } from 'components/Text';
import { TipVerified } from 'components/TipVerified';
import { TipWasImported } from 'components/TipWasImported';

import styles from './Header.module.scss';

interface HeaderProps {
  author: string;
  title: string;
  rating: number;
  publishDate: string;
  location?: string;
  verified?: boolean;
  source: ReviewSource;
}

type AnyReviewsWidget =
  | ExtendedReviewsWidgetSettings
  | ExtendedCarouselWidgetSettings
  | ExtendedFullPageWidgetSettings
  | ExtendedReviewsTabWidgetSettings
  | ExtendedTopRatedProductsWidgetSettings;

export function Header({
  author,
  title,
  rating,
  publishDate,
  location,
  verified,
  source,
}: HeaderProps) {
  const { settings, show_date_creation } = useSettings<AnyReviewsWidget>();

  const locationMarkup = location ? (
    <div
      className={clsx(
        styles.Item,
        styles['Item--third'],
        'wbtxReviewCard__location',
      )}
    >
      <Stack spacing="tight" wrap={false} alignment="center">
        <Icon source={LocationIcon} />
        <Text size="small" as="span">
          {location}
        </Text>
      </Stack>
    </div>
  ) : null;

  const verifiedMarkup = verified ? (
    <Stack spacing="tight" classNames={['wbtxReviewCard__verification']}>
      <Icon source={CircleTickIcon} />
      <TipVerified source={source} />
    </Stack>
  ) : null;

  const isShowDate =
    !settings.reviews?.tweaks.hide_date_creation && show_date_creation;

  const publishDateMarkup = isShowDate && (
    <Text
      variation="subdued"
      size="small"
      classNames={['wbtxReviewCard__publishDate']}
    >
      {publishDate}
    </Text>
  );

  const titleMarkup = !settings.reviews?.review_form.hide_review_title && (
    <div
      className={clsx(
        styles.Item,
        styles['Item--fill'],
        styles['Item--second'],
        'wbtxReviewCard__titleWrapper',
      )}
    >
      <Text size="large" variation="semiStrong">
        {title}
      </Text>
    </div>
  );

  return (
    <Stack vertical classNames={['wbtxReviewCard__header']}>
      <Stack.Item fill>
        <Stack alignment="center">
          <Stack.Item fill>
            <Stack
              alignment="center"
              spacing="tight"
              classNames={['wbtxReviewCard__author-info']}
            >
              <Text
                size="small"
                variation="semiStrong"
                classNames={['wbtxReviewCard__author-name']}
              >
                {author}
              </Text>
              {verifiedMarkup}
              <TipWasImported source={source} />
            </Stack>
          </Stack.Item>
          {publishDateMarkup}
        </Stack>
      </Stack.Item>
      <div className={styles.Grid}>
        <div
          className={clsx(
            styles.Item,
            styles['Item--first'],
            'wbtxReviewCard__rating',
          )}
        >
          <Stars value={rating} />
        </div>
        {titleMarkup}
        {locationMarkup}
      </div>
    </Stack>
  );
}
